import { v4 } from 'uuid';
import { shouldShowBulkUploaderUi } from './shouldShowBulkUploaderUi.js';
import { UploadErrorCode } from '@fabric-app/types/upload';
import { UploadError } from '../upload/upload.error.js';

const MAX_TREE_DEPTH_ALLOWED = 10;
const MAX_AUTO_RETRIES = 3;
var FileStatus = /* @__PURE__ */ function(FileStatus2) {
  FileStatus2["Ready"] = "ready";
  FileStatus2["Retrying"] = "retrying";
  FileStatus2["Uploading"] = "uploading";
  FileStatus2["Finalizing"] = "finalizing";
  FileStatus2["Success"] = "success";
  FileStatus2["Failed"] = "failed";
  return FileStatus2;
}({});
class BulkUploader {
  constructor(options = {}) {
    this.files = [];
    this.uploadSpeed = 0;
    this.running = false;
    this.options = options;
  }
  addFile(file) {
    const { onFileUpdate } = this.options;
    const uploadFile = {
      ...file,
      id: v4(),
      autoRetries: 0,
      progress: 0,
      status: "ready"
    };
    this.files.push(uploadFile);
    onFileUpdate?.(uploadFile);
    this.processFiles();
  }
  addFiles(files) {
    const { onFileUpdate } = this.options;
    const uploadFiles = files.map((file) => ({
      ...file,
      id: v4(),
      autoRetries: 0,
      progress: 0,
      status: "ready",
      processed: false
    }));
    this.files.push(...uploadFiles);
    uploadFiles.forEach((file) => onFileUpdate?.(file));
    this.processFiles();
    this.options.onFilesAdded?.(uploadFiles, (file) => {
      const newFile = {
        ...file,
        processed: true
      };
      this.onFileUpdate(newFile);
      this.processFiles();
    });
  }
  async onSpeedUpdate(speed) {
    const { onSpeedUpdate } = this.options;
    this.uploadSpeed = speed;
    onSpeedUpdate?.(speed);
  }
  async onFileProgress(file, progress, speed) {
    const { onFileUpdate } = this.options;
    const newFile = {
      ...file,
      progress
    };
    onFileUpdate?.(newFile);
    this.onSpeedUpdate(speed);
  }
  async onFileUpdate(file) {
    const { onFileUpdate } = this.options;
    this.files = this.files.map((f) => f.id === file.id ? file : f);
    onFileUpdate?.(file);
  }
  async onUploadComplete(file, response) {
    const { onUploadComplete } = this.options;
    const newFile = {
      ...file,
      status: "success",
      progress: 100
    };
    this.onFileUpdate(newFile);
    onUploadComplete?.(newFile, response, this.onFileUpdate.bind(this));
  }
  async processFiles() {
    const { concurrency = 1 } = this.options;
    this.running = true;
    const uploadingFiles = this.files.filter((file) => file.status === "uploading").length;
    if (uploadingFiles >= concurrency) {
      return;
    }
    const readyFiles = this.files.filter((file) => (file.status === "ready" || file.status === "retrying") && file.processed).sort((a, b) => a.autoRetries - b.autoRetries).slice(0, concurrency - uploadingFiles);
    if (readyFiles.length === 0) {
      return;
    }
    const disableToast = shouldShowBulkUploaderUi(readyFiles);
    readyFiles.forEach(async (file) => {
      const { uploadFunction } = this.options;
      if (!uploadFunction) {
        throw new Error("No upload function provided");
      }
      try {
        const abortController = new AbortController();
        const newFile = {
          ...file,
          status: "uploading",
          abortController
        };
        this.onFileUpdate(newFile);
        const response = await uploadFunction(newFile, this.onFileProgress.bind(this, newFile), () => {
          const newFile2 = {
            ...file,
            status: "finalizing",
            progress: 100
          };
          this.onFileUpdate(newFile2);
        }, abortController.signal, {
          disableToast
        });
        this.onUploadComplete(newFile, response);
      } catch (error) {
        if (error instanceof UploadError) {
          if (error.retryable && file.autoRetries < MAX_AUTO_RETRIES) {
            const newFile = {
              ...file,
              status: "retrying",
              progress: 0,
              autoRetries: file.autoRetries + 1
            };
            this.onFileUpdate(newFile);
          } else {
            const newFile = {
              ...file,
              status: "failed",
              failureReason: error.message,
              errorCode: error.code,
              progress: 0,
              autoRetries: 0
            };
            this.onFileUpdate(newFile);
          }
        } else if (error instanceof Error) {
          const newFile = {
            ...file,
            status: "failed",
            failureReason: error.message,
            errorCode: UploadErrorCode.Unknown,
            progress: 0,
            autoRetries: 0
          };
          this.onFileUpdate(newFile);
        } else {
          console.error("Unknown error type", error);
        }
      }
      this.processFiles();
    });
  }
  retryFile(id) {
    const file = this.files.find((file2) => file2.id === id);
    if (!file) {
      throw new Error("File not found");
    }
    if (file.status !== "failed") {
      throw new Error("File is not in a failed state");
    }
    const newFile = {
      ...file,
      status: "ready",
      progress: 0,
      autoRetries: 0,
      failureReason: void 0,
      errorCode: void 0
    };
    this.onFileUpdate?.(newFile);
    this.processFiles();
  }
  cancelUpload(id) {
    const file = this.files.find((file2) => file2.id === id);
    if (!file) {
      throw new Error("File not found");
    }
    file.abortController?.abort();
    const newFile = {
      ...file,
      status: "failed",
      failureReason: "Cancelled by user",
      progress: 0
    };
    this.onFileUpdate(newFile);
    this.processFiles();
  }
  getFiles() {
    return this.files;
  }
}

export { FileStatus, MAX_TREE_DEPTH_ALLOWED, BulkUploader as default };
//# sourceMappingURL=uploader.js.map
